import React, { useMemo } from 'react';
import classNames from 'classnames';
import RadioGroupField from 'components/RadioGroupField';
import { useFormState } from 'react-final-form';
import {
  WorkflowTemplateValues,
  STEP_COMPLETION_ACTION,
} from 'models/WorkflowConfiguration';
import { useTranslation } from 'react-i18next';
import { LEAD_STAGES, LEAD_STAGES_COPY, LeadStages } from 'models/Lead';
import { getIsCompletionAction } from '../WorkflowTemplate/utils';
import AutomationQuestion from '../AutomationQuestion';
import { CheckboxGroupPlaceholders } from '../WorkflowTemplate/checkboxplaceholders';
import useLeadStagesCopy from 'hooks/useLeadStagesCopy';

import './AutomationProgresser.styles.scss';

const NEXT_STATUS = {
  [LEAD_STAGES.ENQUIRE]: LEAD_STAGES_COPY.ACCEPTED,
  [LEAD_STAGES.ACCEPT]: LEAD_STAGES_COPY.PROSPECT,
  [LEAD_STAGES.CONFIRM]: LEAD_STAGES_COPY.BIDDER,
  [LEAD_STAGES.OFFER]: '',
} as const;

type AutomationProgresserProps = {
  className?: string;
  isLoading?: boolean;
  page: LeadStages;
};

const AutomationProgresser: React.FC<AutomationProgresserProps> = (props) => {
  const { className, page, isLoading } = props;

  const classes = classNames('automation-progresser', className);

  const { t } = useTranslation();
  const {
    values: { completionAction },
  } = useFormState<WorkflowTemplateValues>();

  const copies = useLeadStagesCopy().getCopies();

  const nextStatus = t(NEXT_STATUS[page]);

  const options = useMemo(
    () => [
      {
        label: t('keepInStatus', { status: t(copies[page]) }),
        value: STEP_COMPLETION_ACTION.KEEP,
      },
      {
        label: t('askProgressionQuestion'),
        value: STEP_COMPLETION_ACTION.ASK_PROGRESSION_QUESTION,
      },
      {
        label: t('moveToStatusAutomatically', { status: nextStatus }),
        value: STEP_COMPLETION_ACTION.AUTO_ADVANCE,
      },
    ],
    [nextStatus, page, copies, t],
  );

  if (!nextStatus) return null;

  return (
    <div className={classes}>
      <div className="automation-progresser__content">
        <p className="wft-title">{t('whenAllStageFilesAreAccessed')}</p>
        <p className="automation-progresser__description">
          {t('chooseActionAfterAllFilesAccessed')}
        </p>

        {isLoading ? (
          <CheckboxGroupPlaceholders />
        ) : (
          <RadioGroupField name="completionAction" options={options} />
        )}
      </div>

      {!isLoading && getIsCompletionAction(completionAction) && (
        <AutomationQuestion status={nextStatus} />
      )}
    </div>
  );
};

export default AutomationProgresser;
